/**
 * Geometry epochs: a content fingerprint for each drawn-geometry payload the
 * server builds (subway / LIRR / MNR / PATH route maps).
 *
 * Drawn geometry is fetched once per page load and cached 12h (browser + CF
 * edge, keyed by `?v=<schema version>`), while live train paths stream over
 * the WS. A deploy rebuilds both together, but an already-open tab keeps its
 * old drawn lines — so bullets ride new offset lanes against stale ribbons.
 * The server hashes each payload once when it is built and announces the
 * epochs in the WS hello/snapshot; a client whose drawn epoch differs refetches
 * with `&e=<epoch>`, which is also a brand-new CDN cache key.
 *
 * Every field here is additive and optional on the wire: old clients ignore
 * the extra field, and new clients treat a missing one as "nothing announced".
 */
/** Drawn-geometry payloads that carry an epoch, one per `/api/*-geometry` endpoint. */
export const GEOMETRY_KEYS = ['subway', 'lirr', 'mnr', 'path'];
/**
 * Response header carrying the epoch of the payload actually served. It rides
 * along with the edge-cached body, so a client always learns the epoch of the
 * bytes it drew — even when the CDN served a copy from an older build.
 */
export const GEOMETRY_EPOCH_HEADER = 'X-Vitals-Geometry-Epoch';
/** Query parameter a client appends to request a specific epoch (cache key only). */
export const GEOMETRY_EPOCH_PARAM = 'e';
/** Epoch wire format: first 16 hex chars of a SHA-256 digest (URL-safe by construction). */
const EPOCH_PATTERN = /^[0-9a-f]{16}$/;
export function isGeometryEpoch(value) {
    return typeof value === 'string' && EPOCH_PATTERN.test(value);
}
function isGeometryKey(key) {
    return GEOMETRY_KEYS.includes(key);
}
/**
 * Validate an untrusted `geometry` field from the wire. Unknown keys and
 * malformed epochs are dropped; returns null when nothing valid remains so
 * callers can skip a no-op announcement.
 */
export function parseGeometryEpochs(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return null;
    const entries = Object.entries(raw).filter((entry) => isGeometryKey(entry[0]) && isGeometryEpoch(entry[1]));
    return entries.length > 0 ? Object.fromEntries(entries) : null;
}
/** Append `e=<epoch>` to a geometry URL; a missing or malformed epoch leaves it unchanged. */
export function withGeometryEpoch(url, epoch) {
    if (!isGeometryEpoch(epoch))
        return url;
    return `${url}${url.includes('?') ? '&' : '?'}${GEOMETRY_EPOCH_PARAM}=${epoch}`;
}
